import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { buildFolderPath, DOC_TYPES, normalizeDocType } from "../../services/contractService";
import { reviewStatusService } from "../../services/reviewStatusService";

const getDocType = (id) => DOC_TYPES.find(d=>d.id===id)||DOC_TYPES[DOC_TYPES.length-1];
const getFileIcon = (n) => ({pdf:"[PDF]",docx:"[DOC]",doc:"[DOC]",xlsx:"[XLS]",xls:"[XLS]",pptx:"[PPT]",ppt:"[PPT]",png:"[IMG]",jpg:"[IMG]",jpeg:"[IMG]"})[(n||"").split(".").pop().toLowerCase()]||"[FILE]";
const fmtSize = (b) => !b?"":b<1024?b+"B":b<1048576?Math.round(b/1024)+"KB":(b/1048576).toFixed(1)+"MB";

const guessDocType = (name) => {
  const n = (name||"").toLowerCase();
  let oldType = "other";
  if (n.includes("nda") || n.includes("non-disclosure")) oldType = "executed_contract";
  else if (n.includes("addend") || n.includes("amendment")) oldType = "addendum";
  else if (n.includes("craf") || n.includes("approval")) oldType = "approval";
  else if (n.includes("award")) oldType = "award_letter";
  else if (n.includes("receipt") || n.includes("invoice")) oldType = "receipt";
  else if (n.includes("completion") || n.includes("certificate") || n.includes("cert")) oldType = "completion_cert";
  else if (n.includes("insurance") || n.includes("bond")) oldType = "insurance";
  else if (n.includes("scope") || n.includes("sow")) oldType = "scope_of_work";
  else if (n.includes("kpi") || n.includes("performance")) oldType = "kpi";
  else if (n.includes("pricing") || n.includes("rate")) oldType = "pricing";
  else if (n.includes("draft")) oldType = "contract_draft";
  else {
    const ext = n.split(".").pop();
    if (["eml","msg"].includes(ext)) oldType = "correspondence";
    else if (/[a-z]+-\d/.test(n) && ["docx","doc"].includes(ext)) oldType = "contract_draft";
    else if (/[a-z]+-\d/.test(n) && ext === "pdf") oldType = "original_document";
  }
  return normalizeDocType(oldType);
};

const DocCard = ({ doc }) => {
  const t = getDocType(doc.DocType);
  const isWordDoc = doc.FileName.toLowerCase().endsWith('.docx') || doc.FileName.toLowerCase().endsWith('.doc');
  return (
    <div className="bg-transparent border border-slate-700 rounded-lg p-3 md:p-4 flex items-center gap-3 md:gap-4 transition-all hover:border-slate-600">
      <div className="text-lg md:text-xl shrink-0 font-mono">{getFileIcon(doc.FileName)}</div>
      <div className="flex-1 overflow-hidden min-w-0">
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <span className="text-sm md:text-base font-medium text-slate-100 overflow-hidden text-ellipsis whitespace-nowrap">{doc.FileName}</span>
          {isWordDoc && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-orange-900/20 text-orange-400 whitespace-nowrap shrink-0">📝 Changes</span>}
        </div>
        {doc.Description&&<div className="text-xs md:text-sm text-slate-400 mb-1 leading-relaxed">{doc.Description}</div>}
        <div className="flex gap-4 text-xs text-slate-400 flex-wrap">
          {doc.FileSize?<span>{fmtSize(parseInt(doc.FileSize))}</span>:null}
          {doc.UploadedBy?<span>by {doc.UploadedBy.split("@")[0]}</span>:null}
          {doc.UploadedDate?<span>{new Date(doc.UploadedDate).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>:null}
        </div>
      </div>
      <div className="shrink-0">
        <a href={doc.WebUrl} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-900/20 border border-blue-600/30 rounded-lg text-blue-400 text-xs font-medium hover:text-blue-300 hover:border-blue-500/50 transition-colors whitespace-nowrap">
          Open ↗
        </a>
      </div>
    </div>
  );
};

export const ReviewerMode = () => {
  const { service: graphService } = useApp();
  const { id: contractId, token } = useParams();
  const [contract, setContract] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driveId, setDriveId] = useState(null);
  const [reviewed, setReviewed] = useState(false);
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState("");
  const [reviewId, setReviewId] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("Not Started"); // Not Started, In Progress, Complete
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [reviewComment, setReviewComment] = useState("I have reviewed this contract and confirm that I am satisfied with the terms and conditions presented.");
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    const validateAndLoad = async () => {
      try {
        setValidatingToken(true);

        // Validate token - match the format used in ContractDetail.generateReviewerToken
        // Token is btoa(contractId + "_reviewer_" + date).substring(0, 12)
        let tokensMatch = false;

        // Check tokens from past 30 days to account for timezone differences
        for (let i = 0; i < 30; i++) {
          const checkDate = new Date();
          checkDate.setDate(checkDate.getDate() - i);
          const dateStr = checkDate.toISOString().split("T")[0];
          const expectedToken = btoa(contractId + "_reviewer_" + dateStr).substring(0, 12);
          if (token === expectedToken) {
            tokensMatch = true;
            console.log('[ReviewerMode] ✅ Token validated for contract', contractId);
            break;
          }
        }

        if (!tokensMatch) {
          console.error('[ReviewerMode] ❌ Token validation failed. Token:', token, 'ContractId:', contractId);
          setError("Invalid sharing link. Please request a new one from the admin.");
          setValidatingToken(false);
          return;
        }

        setTokenValid(true);

        // Load contract (minimal data)
        const contractData = await graphService.getListItem("Contracts", contractId);
        if (!contractData) {
          setError("Contract not found.");
          setValidatingToken(false);
          return;
        }

        const f = { ...contractData.fields, id: contractData.id };
        setContract(f);

        // Fetch ReviewStatus record to get reviewId and current status
        try {
          const reviewStatusData = await graphService.getListItems("ReviewStatus");
          const reviewStatuses = reviewStatusData?.value || [];
          // Find the first ReviewStatus record for this contract using ContractIDLookupId
          const reviewStatusRecord = reviewStatuses.find(rs => rs.fields.ContractIDLookupId === contractId || rs.fields.ContractIDLookupId?.toString() === contractId?.toString());
          if (reviewStatusRecord?.id) {
            setReviewId(reviewStatusRecord.id);
            const status = reviewStatusRecord.fields.Status || "Not Started";
            setReviewStatus(status);
            console.log('[ReviewerMode] ✅ Found ReviewStatus record:', reviewStatusRecord.id, 'Status:', status, 'ContractIDLookupId:', reviewStatusRecord.fields.ContractIDLookupId);

            // If review is already complete, block access
            if (status === "Complete") {
              setAccessBlocked(true);
              console.log('[ReviewerMode] Review is complete - blocking access');
            }
          } else {
            console.error('[ReviewerMode] ❌ No ReviewStatus record found for contract:', contractId);
            console.log('[ReviewerMode] Available records:', reviewStatuses.map(rs => ({ id: rs.id, ContractIDLookupId: rs.fields.ContractIDLookupId, Status: rs.fields.Status })));
          }
        } catch (e) {
          console.warn('[ReviewerMode] Could not load ReviewStatus:', e);
        }

        // Load documents
        const folderPath = buildFolderPath(f.Entity, f.Department, f.Year||new Date().getFullYear(), f.Month||1, f.RefNo||"");
        const drives = await graphService.getDrives();
        const docsDrive = drives?.value?.find(d => d.name === "ContractDocuments");

        if (docsDrive) {
          setDriveId(docsDrive.id);
          try {
            const folderContents = await graphService.getFolderContents(docsDrive.id, folderPath);
            const items = (folderContents?.value||[]).filter(i=>!i.folder).map(i=>({
              id: i.id,
              FileName: i.name,
              WebUrl: i.webUrl,
              FileSize: String(i.size||0),
              DocType: guessDocType(i.name),
              UploadedDate: i.lastModifiedDateTime||i.createdDateTime,
              UploadedBy: i.createdBy?.user?.displayName||i.lastModifiedBy?.user?.displayName||"",
            }));
            setDocs(items);
          } catch (e) {
            console.error("Error loading documents:", e);
            setDocs([]);
          }
        }
      } catch (e) {
        console.error("Error loading contract:", e);
        setError("Failed to load contract. Please check your link.");
      } finally {
        setValidatingToken(false);
        setLoading(false);
      }
    };

    validateAndLoad();
  }, [contractId, token, graphService]);

  const handleUnlockAndReview = async () => {
    if (!reviewerName.trim()) {
      alert("Please enter your name to start reviewing.");
      return;
    }
    if (!reviewerEmail.trim()) {
      alert("Please enter your email address to start reviewing.");
      return;
    }

    setUnlocking(true);
    try {
      // Step 1: Find EXACT ReviewStatus record matching contract ID and email
      let exactReviewId = reviewId;
      try {
        console.log('[ReviewerMode] Searching for ReviewStatus with email:', reviewerEmail);
        const reviewStatusData = await graphService.getListItems("ReviewStatus");
        const reviewStatuses = reviewStatusData?.value || [];

        // Log all found ReviewStatus records for debugging
        console.log('[ReviewerMode] Found ReviewStatus records:', reviewStatuses.map(rs => ({
          id: rs.id,
          contractId: rs.fields.ContractID,
          email: rs.fields.ReviewerEmail,
          status: rs.fields.Status
        })));

        // Find ReviewStatus matching both contract and email
        console.log('[ReviewerMode] Searching for match with ContractIDLookupId:', contractId, 'Type:', typeof contractId);
        console.log('[ReviewerMode] ReviewStatus records and their ContractIDLookupIds:', reviewStatuses.map(rs => ({
          id: rs.id,
          ContractIDLookupId: rs.fields.ContractIDLookupId,
          ContractIDLookupIdType: typeof rs.fields.ContractIDLookupId,
          Email: rs.fields.ReviewerEmail
        })));

        const exactMatch = reviewStatuses.find(rs => {
          const idMatch = rs.fields.ContractIDLookupId === contractId || rs.fields.ContractIDLookupId?.toString() === contractId?.toString();
          const emailMatch = rs.fields.ReviewerEmail?.toLowerCase() === reviewerEmail.toLowerCase();
          console.log('[ReviewerMode] Checking match:', {
            rsId: rs.id,
            idMatch,
            emailMatch,
            rsContractIDLookupId: rs.fields.ContractIDLookupId,
            contractId
          });
          return idMatch && emailMatch;
        });

        if (exactMatch?.id) {
          exactReviewId = exactMatch.id;
          setReviewId(exactReviewId);
          console.log('[ReviewerMode] ✅ Found EXACT ReviewStatus match:', exactReviewId, 'Email:', reviewerEmail);
        } else {
          console.error('[ReviewerMode] ❌ No exact ReviewStatus match found. Looking for ContractIDLookupId:', contractId, 'Email:', reviewerEmail);
          console.log('[ReviewerMode] Available ReviewStatus records:', reviewStatuses.map(rs => ({ id: rs.id, ContractIDLookupId: rs.fields.ContractIDLookupId, Email: rs.fields.ReviewerEmail })));
        }
      } catch (e) {
        console.warn('[ReviewerMode] Error searching ReviewStatus:', e);
      }

      // Step 2: Mark review as started (updates ReviewStatus list to "In Progress")
      console.log('[ReviewerMode] Calling markReviewStarted with id:', exactReviewId);
      try {
        await reviewStatusService.markReviewStarted(graphService, {
          contractId,
          contractRef: contract?.RefNo || "",
          contractTitle: contract?.Title || "",
          reviewerName,
          reviewerEmail,
          id: exactReviewId
        });
        console.log('[ReviewerMode] ✅ markReviewStarted succeeded');
      } catch (error) {
        console.error('[ReviewerMode] ❌ markReviewStarted failed:', error);
        alert('Error updating review status: ' + error.message + '\n\nPlease contact the admin.');
        setUnlocking(false);
        return;
      }

      // Step 3: Log reviewer action
      try {
        await graphService.createListItem("NotificationLog", {
          Title: `Reviewer_Unlocked_${contractId}`,
          ContractID: contractId,
          ContractRefNo: contract?.RefNo || contractId,
          NotificationType: "reviewer_unlocked",
          RecipientEmails: contract?.RequestedBy || "admin",
          Subject: `Review Unlocked: ${contract?.Title || "Contract"} - ${reviewerName}`,
          SentAt: new Date().toISOString(),
          Status: "sent",
        });
      } catch (e) {
        console.warn('[ReviewerMode] Could not log notification:', e);
      }

      // Step 4: Show documents
      setReviewed(true);
      setReviewStatus("In Progress");
      console.log('[ReviewerMode] ✅ Review unlocked and started');
    } catch (error) {
      console.error('[ReviewerMode] Unexpected error:', error);
      alert('Error starting review: ' + error.message);
    } finally {
      setUnlocking(false);
    }
  };

  const handleCompleteReview = async (comment = "") => {
    setCompleting(true);
    try {
      console.log('[ReviewerMode] Marking review as complete with id:', reviewId);

      // Mark review as complete in ReviewStatus list
      try {
        await reviewStatusService.markReviewComplete(graphService, {
          contractId: contractId,
          contractRef: contract?.RefNo || "",
          contractTitle: contract?.Title || "",
          reviewerName: reviewerName,
          reviewerEmail: reviewerEmail,
          id: reviewId
        });
        console.log('[ReviewerMode] ✅ markReviewComplete succeeded');
      } catch (error) {
        console.error('[ReviewerMode] ❌ markReviewComplete failed:', error);
        alert('Error marking review complete: ' + error.message + '\n\nPlease contact the admin.');
        setCompleting(false);
        return;
      }

      // Update contract status back to "Requested"
      try {
        console.log('[ReviewerMode] Updating contract status back to "Requested"');
        await graphService.updateListItem("Contracts", contractId, {
          Status: "Requested"
        });
        console.log('[ReviewerMode] ✅ Contract status updated to "Requested"');
      } catch (e) {
        console.warn('[ReviewerMode] Could not update contract status:', e);
      }

      // Send email notification to ALL admins
      try {
        const allUsers = await graphService.getListItems("AllowedUsers");
        const adminEmails = (allUsers?.value || [])
          .filter(u => u.fields.Role === 'admin' || u.fields.Role === 'Admin')
          .map(u => u.fields.Title)
          .filter(email => email && email.trim());

        if (adminEmails.length > 0) {
          const emailSubject = `Review Completed: ${contract?.RefNo} - ${contract?.Title || "Contract"}`;
          const commentSection = comment ? `
            <div style="background-color: #e8f5e9; padding: 15px; border-left: 4px solid #4caf50; border-radius: 4px; margin: 20px 0;">
              <p style="margin: 0 0 10px 0;"><strong style="color: #2e7d32;">Reviewer's Comments:</strong></p>
              <p style="margin: 0; color: #1b5e20; white-space: pre-wrap;">${comment}</p>
            </div>
          ` : "";

          const emailBody = `
            <p>Hi,</p>
            <p><strong>${reviewerName}</strong> has completed their review of the following contract:</p>
            <div style="background-color: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0;">
              <p><strong>Contract:</strong> ${contract?.Title || "Untitled"}</p>
              <p><strong>Reference:</strong> ${contract?.RefNo}</p>
              <p><strong>Reviewed by:</strong> ${reviewerName} (${reviewerEmail})</p>
              <p><strong>Completed at:</strong> ${new Date().toLocaleString()}</p>
            </div>
            ${commentSection}
            <p>The contract status has been updated to "Requested" and is ready for further processing.</p>
            <p>Log in to LexCore to review the details and any reviewer comments.</p>
            <p>Best regards,<br/>LexCore Contract Management System</p>
          `;

          await graphService.sendEmail(adminEmails, emailSubject, emailBody);
          console.log('[ReviewerMode] ✅ Completion email sent to', adminEmails.length, 'admin(s):', adminEmails);
        } else {
          console.warn('[ReviewerMode] ⚠️ No admins found to notify');
        }
      } catch (e) {
        console.error('[ReviewerMode] Could not send completion email:', e);
      }

      // Log completion
      try {
        await graphService.createListItem("NotificationLog", {
          Title: `Reviewer_Completed_${contractId}`,
          ContractID: contractId,
          ContractRefNo: contract?.RefNo || contractId,
          NotificationType: "reviewer_completed",
          RecipientEmails: contract?.RequestedBy || "admin",
          Subject: `Review Completed: ${contract?.Title || "Contract"} - ${reviewerName}`,
          SentAt: new Date().toISOString(),
          Status: "sent",
        });
      } catch (e) {
        console.warn('[ReviewerMode] Could not log completion:', e);
      }

      // Block access
      setReviewStatus("Complete");
      setAccessBlocked(true);
      setShowCommentModal(false);
      console.log("[ReviewerMode] ✅ Review marked as complete - access blocked");
    } catch (error) {
      console.error("Unexpected error completing review:", error);
      alert('Error: ' + error.message);
    } finally {
      setCompleting(false);
    }
  };

  const handleSubmitReview = async () => {
    setSubmittingComment(true);
    await handleCompleteReview(reviewComment);
    setSubmittingComment(false);
  };

  const handleOpenCommentModal = () => {
    setShowCommentModal(true);
  };

  if (validatingToken || loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-700 border-t-yellow-600 rounded-full animate-spin mx-auto mb-4"/>
          <p className="text-slate-400">Loading...</p>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-5">
        <div className="bg-slate-800 border border-red-700 rounded-xl p-8 max-w-md text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-red-600 font-serif text-2xl mb-3">Access Denied</h2>
          <p className="text-slate-400 m-0 leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  if (!contract) return null;

  // If access is blocked, show the access denied message
  if (accessBlocked) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-5">
        <div className="bg-slate-800 border border-green-700 rounded-xl p-12 max-w-2xl text-center">
          <div className="text-8xl mb-6">✅</div>
          <h2 className="font-serif text-4xl font-semibold text-green-600 mb-4">Review Complete</h2>
          <p className="text-slate-400 mb-6 leading-relaxed">
            Thank you for reviewing <strong>{contract.Title}</strong> ({contract.RefNo}).
          </p>
          <p className="text-slate-400 mb-8 leading-relaxed">
            Your review has been submitted and recorded. You no longer have access to this document. The admin team has been notified of your completion.
          </p>
          <div className="bg-slate-700/50 border border-dashed border-slate-600 rounded-lg p-4 mb-6">
            <p className="text-slate-400 text-sm m-0">Contract Ref: <strong className="text-slate-100">{contract.RefNo}</strong></p>
            <p className="text-slate-400 text-sm mt-2 m-0">Completed: <strong className="text-slate-100">{new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</strong></p>
          </div>
          <p className="text-slate-400 text-xs m-0">You can now close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-5">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 pb-5 border-b border-slate-700">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-10 h-10 bg-yellow-600 rounded-lg flex items-center justify-center text-xl">📋</div>
            <div className="flex-1">
              <h1 className="font-serif text-2xl md:text-3xl font-medium text-slate-100 m-0">{contract.Title}</h1>
              <p className="text-sm text-slate-400 mt-1">Reference: {contract.RefNo}</p>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl relative overflow-hidden">
          <div className="p-3 md:p-5 bg-slate-700/50 border-b border-slate-700">
            <div className="font-serif text-lg md:text-xl font-medium text-slate-100">Contract Documents</div>
            <div className="text-sm text-slate-400 mt-1">{docs.length} file{docs.length!==1?"s":""} to review</div>
          </div>

          {/* Seal UI - shows until review starts (only if not already started) */}
          {!reviewed && reviewStatus === "Not Started" && (
            <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto">
              <div className="text-center px-5 py-12 my-auto">
                <div className="text-7xl md:text-8xl mb-12">🔒</div>
                <h2 className="font-serif text-3xl md:text-4xl font-medium text-slate-100 mb-4">Start Reviewing</h2>
                <p className="text-slate-400 mb-12 leading-relaxed">Enter your details to access the contract documents</p>

                <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-8 md:p-12 w-full max-w-md mx-auto flex flex-col gap-6">
                  <div className="text-left">
                    <label className="block text-xs md:text-sm font-semibold text-slate-400 mb-3">Full Name *</label>
                    <input
                      type="text"
                      placeholder="Enter your full name"
                      value={reviewerName}
                      onChange={(e)=>setReviewerName(e.target.value)}
                      className="w-full px-3 md:px-4 py-3 md:py-4 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-yellow-600"
                      autoFocus
                    />
                  </div>
                  <div className="text-left">
                    <label className="block text-xs md:text-sm font-semibold text-slate-400 mb-3">Email Address *</label>
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={reviewerEmail}
                      onChange={(e)=>setReviewerEmail(e.target.value)}
                      onKeyPress={(e)=>e.key==="Enter"&&handleUnlockAndReview()}
                      className="w-full px-3 md:px-4 py-3 md:py-4 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-yellow-600"
                    />
                  </div>
                  <button
                    onClick={handleUnlockAndReview}
                    disabled={unlocking || !reviewerName.trim() || !reviewerEmail.trim()}
                    className={`w-full px-6 py-3 md:py-4 rounded-lg font-semibold text-sm md:text-base transition-all ${
                      unlocking || !reviewerName.trim() || !reviewerEmail.trim()
                        ? "bg-yellow-600/50 text-slate-900 cursor-not-allowed opacity-70"
                        : "bg-yellow-600 hover:bg-yellow-700 text-slate-900 cursor-pointer"
                    }`}
                  >
                    {unlocking ? "Unlocking..." : "🔓 Unlock & Review"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Documents List */}
          <div className="p-5">
            {docs.length===0?(
              <div className="text-center py-9">
                <div className="text-6xl mb-3 opacity-40">📋</div>
                <div className="text-sm text-slate-400">No documents attached to this contract.</div>
              </div>
            ):(
              <div className="flex flex-col gap-1.5">
                {docs.map((doc,i)=><DocCard key={doc.id||i} doc={doc}/>)}
              </div>
            )}
          </div>
        </div>

        {/* Complete Review Button */}
        {(reviewed || reviewStatus === "In Progress") && (
          <div className="mt-6 text-center">
            <button
              onClick={handleOpenCommentModal}
              disabled={completing}
              className={`px-8 py-3 md:py-4 rounded-lg font-semibold text-sm md:text-base transition-all ${
                completing
                  ? "bg-green-700/50 text-white cursor-not-allowed opacity-70"
                  : "bg-green-700 hover:bg-green-800 text-white cursor-pointer"
              }`}
            >
              {completing ? "Completing..." : "✓ Complete Review"}
            </button>
            <p className="text-slate-400 text-xs mt-3">Your access will be blocked once you complete the review</p>
          </div>
        )}

        {/* Comment Modal */}
        {showCommentModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-5">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="font-serif text-2xl font-medium text-slate-100 mb-4">Review Summary</h2>
              <p className="text-slate-400 mb-6">Please add any comments about your review (optional). This will be sent to the admin along with your completion notification.</p>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-3">Your Comments:</label>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  rows={6}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-green-600 resize-vertical"
                  placeholder="Add optional comments about your review..."
                />
                <p className="text-slate-500 text-xs mt-2">Character limit: {reviewComment.length} / 500</p>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowCommentModal(false)}
                  disabled={submittingComment || completing}
                  className="px-6 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-300 cursor-pointer text-sm hover:bg-slate-600 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReview}
                  disabled={submittingComment || completing}
                  className={`px-6 py-2.5 rounded-lg font-semibold text-sm text-white transition-all ${
                    submittingComment || completing
                      ? "bg-green-700/50 cursor-not-allowed opacity-70"
                      : "bg-green-700 hover:bg-green-800 cursor-pointer"
                  }`}
                >
                  {submittingComment || completing ? "Submitting..." : "✓ Submit Review"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 p-5 bg-slate-800 border border-slate-700 rounded-xl text-center">
          <p className="text-slate-400 text-xs m-0">This is a confidential document shared for review purposes only. Do not share this link with others.</p>
        </div>
      </div>
    </div>
  );
};
